import { provider } from '@/provider.js'
import Helpers from "@/helpers.js"
import yaml from 'js-yaml'

export default {

  prepareNewCaseTemplate({ commit }) {
    commit('setCurrentCaseTemplate', { caseTypeId: 'new' })
  },

  setCurrentCaseTemplate({ commit }, { caseTypeId }) {
    commit('setCurrentCaseTemplate', { caseTypeId })
  },

  async setCaseTypeBuckets({ commit }, caseTypeId) {
    const response = await provider.getBucketsByCaseType(caseTypeId)
    if (response && response.buckets) commit('setCaseTypeBuckets', { caseTypeId, buckets: response.buckets })
  },

  updateSchema({ commit }, schema) {
    if (schema) {
      commit('setCurrentCaseTemplateSchema', schema)
    }
  },

  updateRelationships({ commit }, relationships) {
    commit('setCurrentCaseTemplateRelationships', relationships)
  },

  async createScript({ commit, state }, scriptData) {
    const response = await provider.createScript(scriptData)
    commit("displayFlashMessage", { message: response.message, severity: response.error ? "error" : "success" })
    if (!response.error) {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
      console.log("action done")
      commit("createScript", { script: response, caseTypeId: state.current.id })
      commit("reloadConfigMenu", state.all)
    }
    return response
  },

  async updateScript({ commit }, scriptData) {
    const response = await provider.updateScript(scriptData)
    commit("displayFlashMessage", { message: response.message, severity: response.error ? "error" : "success" })
    if (response && !response.error) {
      commit("upsertSavedScript", response)
    }
    return response
  },

  async executeScript({ commit }, scriptData) {
    const response = await provider.executeScript(scriptData)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    }
    return response
  },

  async getScriptExecutionPhase(_ctx, runId) {
    return provider.getScriptExecutionPhase(runId)
  },

  async deleteScript({ commit, state }, data) {
    commit('taskStarted')
    const response = await provider.deleteScript(data.scriptId)
    if (response.error) { commit('taskUpdate', response) } else {
      delete response.error
      delete response.message
      delete response.lowLevelErrorMessage
      commit("removeScript", data)
      commit("reloadConfigMenu", state.all)
    }
    commit('taskComplete')
    return response
  },

  async createEmailTemplate({ commit, state, rootState }, emailTemplate) {
    const response = await provider.createEmailTemplate(emailTemplate)
    commit("displayFlashMessage", { message: response.message, severity: response.error ? "error" : "success" })
    if (!response.error) {
      emailTemplate.id = response.id
      commit("createEmailTemplate", { emailTemplate, caseTypeId: state.current.id })
      commit("reloadConfigMenu", state.all)
      
      // Update email window templates if compose window is open
      if (rootState.ui && rootState.ui.emailWindow && rootState.ui.emailWindow.emailWindowToggled) {
        commit("ui/updateEmailWindowTemplates", rootState, { root: true })
      }
    }
    return response
  },
  async updateEmailTemplate({ commit }, data) {
    const response = await provider.updateEmailTemplate(data)
    commit("displayFlashMessage", { message: response.message, severity: response.error ? "error" : "success" })
    return response
  },
  async renderEmailTemplate({ commit }, data) {
    const response = await provider.renderEmailTemplate(data)
    commit("displayFlashMessage", { message: response.message, severity: response.error ? "error" : "success" })
    if (!response.error) {
      delete response.error
      delete response.message
    }
    return response
  },

  async deleteEmailTemplate({ commit, state }, data) {
    const response = await provider.deleteEmailTemplate(data.emailTemplateId)
    commit("displayFlashMessage", { message: response.message, severity: response.error ? "error" : "success" })
    if (!response.error) {
      delete response.error
      delete response.message
      commit("removeEmailTemplate", data)
      commit("reloadConfigMenu", state.all)
    }
    return response
  },

  async createCaseType({ commit }, caseType) {
    const data = {
      name: caseType.name,
      description: caseType.description,
      coreTemplate: caseType.coreTemplate,
      schema: caseType.schema,
      iconSet: caseType.iconSet,
      iconCode: caseType.iconCode
    }
    const response = await provider.createCaseType(data)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    }
    return !response.error
  },

  async updateCaseType({ commit, dispatch }, { id, caseType }) {
    if (caseType.schema instanceof Object) {
      caseType.schema = yaml.safeDump(caseType.schema)
    }

    const data = {
      name: caseType.name,
      description: caseType.description,
      uniqueIdentifierField: caseType.uniqueIdentifierField,
      coreTemplate: caseType.coreTemplate,
      displayName: caseType.displayName,
      schema: caseType.schema,
      iconSet: caseType.iconSet,
      iconCode: caseType.iconCode
    }
    try {
      const response = await provider.updateCaseType({ id, data })
      if (response.error) {
        commit("displayFlashMessage", { message: response.message, severity: "error" })
      } else {
        commit("displayFlashMessage", { message: response.message, severity: "success" })
        dispatch("reloadCaseTypes")
      }
    } catch (err) {
      if (err.graphQLErrors && err.graphQLErrors[0] &&
          err.graphQLErrors[0].extensions &&
          err.graphQLErrors[0].extensions.exception &&
          err.graphQLErrors[0].extensions.exception.data &&
          err.graphQLErrors[0].extensions.exception.data.stack) {
        commit("displayFlashMessage", { message: err.graphQLErrors[0].extensions.exception.data.stack.split('\n')[0], severity: "error" })
      } else {
        commit("displayFlashMessage", { message: err.message, severity: "error" })
      }
    }
  },

  async deleteCaseType({ commit }, caseTypeId) {
    const response = await provider.deleteCaseType(caseTypeId)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    }
    return !response.error
  },

  async importAtomicData({ commit }, data) {
    const guid = Helpers.generateUUID()
    const path = data.importData + `${guid}/${data.file.name}`
    await Helpers.uploadFile(data.file, path)
    data.importData = path
    const response = await provider.importAtomicData(data)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    }
    return !response.error
  },

  async importTabularData({ commit }, data) {
    const guid = Helpers.generateUUID()
    const path = data.importData + `${guid}/${data.file.name}`
    await Helpers.uploadFile(data.file, path)
    data.importData = path
    const response = await provider.importTabularData(data)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    }
    return !response.error
  },

  async getFolders({ commit, dispatch }, data) {
    if (data) {
      const loadingMutation = data.dialogDocumentsTable ? 'setDialogFoldersLoading' : 'setFoldersLoading'
      commit(loadingMutation, true)

      if (data.configMode === true) {
        await dispatch('getTemplatedFolders', {
          caseTypeId: data.caseTypeId,
          parentFolderId: data.parentFolderId
        })
      } else {
        await dispatch('getCaseFolders', {
          caseId: data.caseId,
          parentFolderId: data.parentFolderId,
          dialogDocumentsTable: data.dialogDocumentsTable
        })
      }
      commit(loadingMutation, false)
    }
  },

  async getTemplatedFolders({ commit }, { caseTypeId, parentFolderId }) {
    try {
      const response = await provider.getTemplatedFolders(caseTypeId, parentFolderId)
      if (response) {
        commit("setCaseTypeTemplatedFolders", { caseTypeId, templatedFolders: response })
        return response
      } else {
        commit("displayFlashMessage", { message: "Error fetching folders", severity: "error" })
      }
    } catch (err) {
      console.log(err)
      commit("displayFlashMessage", { message: err, severity: "error" })
    }
  },

  async createTemplatedFolder({ commit, state }, data) {
    const { folderName, caseTypeId, parentFolderId } = data
    try {
      const response = await provider.createTemplatedFolder(folderName, caseTypeId, parentFolderId)
      if (response.error) {
        commit("displayFlashMessage", { message: response.message, severity: "error" })
      } else {
        let templatedFolders = []
        if (state.current && state.current.templatedFolders) {
          templatedFolders = state.current.templatedFolders.concat([response.folder])
        } else {
          templatedFolders = [response.folder]
        }
        commit("setCaseTypeTemplatedFolders", { caseTypeId, templatedFolders })
        commit("displayFlashMessage", { message: response.message, severity: "success" })
      }
    } catch (err) {
      console.log(err)
      commit("displayFlashMessage", { message: err, severity: "error" })
    }
  },

  async deleteTemplatedFolder({ commit }, data) {
    const { folderId, caseTypeId } = data
    try {
      const response = await provider.deleteTemplatedFolder(folderId)
      if (response.error) {
        commit("displayFlashMessage", { message: response.message, severity: "error" })
      } else {
        commit("deleteCaseTypeTemplatedFolder", { caseTypeId, folderId })
        commit("displayFlashMessage", { message: response.message, severity: "success" })
      }
    } catch (err) {
      console.log(err)
      commit("displayFlashMessage", { message: err, severity: "error" })
    }
  },

  async updateTemplatedFolder({ commit }, data) {
    const { folderId, folderName, caseTypeId } = data
    try {
      const response = await provider.updateTemplatedFolder(folderId, folderName)
      if (response.error) {
        commit("displayFlashMessage", { message: response.message, severity: "error" })
      } else {
        commit("updateCaseTypeTemplatedFolder", {
          caseTypeId,
          updatedFolder: response.folder
        })
        commit("displayFlashMessage", { message: response.message, severity: "success" })
      }
    } catch (err) {
      console.log(err)
      commit("displayFlashMessage", { message: err, severity: "error" })
    }
  },

  async setFoldersLoading({ commit, dispatch }, state) {
    commit('setFoldersLoading', state)
  },

  async performReindexDocuments({ commit }, data) {
    const response = await provider.reindexDocuments(data)
    if (response && !response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    } else {
      commit("displayFlashMessage", { message: response && response.message ? response.message : "Document reindex failed", severity: "error" })
    }
    return response
  },

  async performReindexCaseType({ commit }, data) {
    const response = await provider.reindexCaseType(data)
    if (response && !response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
    } else {
      commit("displayFlashMessage", { message: response && response.message ? response.message : "Case reindex failed", severity: "error" })
    }
    return response
  }
}
